import { TableCell, TableRow, Badge, Tooltip, TooltipTrigger, TooltipContent } from "@libs/zapi-ui/shadcn";
import { AiLogsByRequestId } from "@/zapi-admin/pages/AiLogsPage/model/types";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import { CalendarBlank, Copy, Coins } from "@phosphor-icons/react";
import { ExecutionTimeBadge } from "./ExecutionTimeBadge";
import { AiLogGroupTable } from "./AiLogGroupTable";
import { useAiLogDetails } from "@/zapi-admin/pages/AiLogsPage/lib/useAiLogDetails";
import { dateIsRecent } from "@/zapi-admin/shared/lib/dates";
import React from "react";
import { toast } from "sonner";
import { Costs } from "@/zapi-admin/pages/AiLogsPage/ui/Costs";

type TAILogsTableRowProps = {
  logGroup: AiLogsByRequestId[0];
  onDetailClick: (logId: number) => void;
  expanded?: boolean;
  setExpanded?: (expanded: boolean) => void;
};

const getRequestExecutionTime = (log: AiLogsByRequestId[0]) => {
  if (!log.rows.length) return null;
  const isDone =
    log.rows.every((row) => typeof row.executionTimeMs === "number") || !dateIsRecent(log.rows[0].createdAt);
  if (!isDone) return null;
  const firstExecutedAt = log.rows[0]?.createdAt;
  const endTimes = log.rows
    .map((row) => {
      if (!row.createdAt || typeof row.executionTimeMs !== "number") return undefined;
      return row.createdAt.getTime() + row.executionTimeMs;
    })
    .filter((t): t is number => typeof t === "number" && !isNaN(t));
  if (!firstExecutedAt || endTimes.length === 0) return null;
  const lastExecutedAt = new Date(Math.max(...endTimes));
  return lastExecutedAt.getTime() - firstExecutedAt.getTime();
};

export function AILogsTableRow({ logGroup, onDetailClick, expanded, setExpanded }: TAILogsTableRowProps) {
  const { data } = useAiLogDetails(Number(logGroup.rows?.[0].id));
  const task = data?.requestParams?.["requestAnnotations"]?.["userMessage"] || logGroup.rows[0]?.label;
  // TODO is cheap and safe to mark as error those with no execution time, for deeper error checks whole data needs to be fetched
  // checking if date is recent to avoid marking old logs as success when no errors field is present
  const isError = logGroup.rows.some(
    (row) => !!row.errors || (row.executionTimeMs === null && !row.cacheHit && !dateIsRecent(row.createdAt))
  );
  const chapters = logGroup.rows.map((r) => r.chapter?.projectTemplateChapterSlug).filter(Boolean);
  const uniqueChapters = Array.from(new Set(chapters));

  const copyTaskIdToClipboard = () => {
    try {
      navigator.clipboard.writeText(logGroup.requestId);
      toast.success("Task ID copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy Task ID");
    }
  };

  const agreggateCosts = (logGroup: AiLogsByRequestId[0]) => {
    let totalRequestTokens = 0;
    let totalCachedTokens = 0;
    let totalResponseTokens = 0;
    let totalCost = 0;
    let source = "exact";
    let costSource = "exact";
    let cacheHit: boolean | null = null;

    logGroup.rows.forEach((row) => {
      if (row.costs) {
        totalRequestTokens += row.costs.tokens?.inputTokens || 0;
        totalCachedTokens += row.costs.tokens?.cacheReadTokens || 0;
        totalResponseTokens += row.costs.tokens?.outputTokens || 0;
        totalCost += row.costs.cost?.total || 0;
        if (row.costs.meta?.source && row.costs.meta.source !== "exact") {
          source = row.costs.meta.source;
        }
        if (row.costs.cost?.source && row.costs.cost.source !== "exact") {
          costSource = row.costs.cost.source;
        }
      }
      // Aggregate cacheHit: if any row has cacheHit === true, the group has cacheHit === true
      if (row.cacheHit === true) {
        cacheHit = true;
      } else if (cacheHit === null && row.cacheHit === false) {
        cacheHit = false;
      }
    });
    return {
      source,
      inputTokens: totalRequestTokens,
      cachedInputTokens: totalCachedTokens,
      outputTokens: totalResponseTokens,
      totalCost,
      costSource,
      cacheHit
    };
  };

  const costs = agreggateCosts(logGroup);

  const projectField = (() => {
    // TODO: Remove this fallback — project data should not be retrieved through the chapter.
    if (logGroup.rows[0]?.project?.id) {
      return `${logGroup.rows[0]?.project?.id}: ${logGroup.rows[0]?.project?.name ?? ""}`;
    }

    if (logGroup.rows[0]?.chapter?.project.id) {
      return `${logGroup.rows[0]?.chapter?.project.id}: ${logGroup.rows[0]?.chapter?.project.name ?? ""}`;
    }

    return "";
  })();

  return (
    <>
      <TableRow
        className={`cursor-pointer group transition ${
          isError ? "bg-[#FFF0F0A6] hover:bg-innoveraRed-50" : "hover:bg-zinc-50"
        }`}
        onClick={() => setExpanded?.(!expanded)}
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setExpanded?.(!expanded);
        }}
      >
        <TableCell className="font-medium min-h-[56px] max-w-[30px] overflow-hidden text-ellipsis">
          <AppButton
            appearance="text"
            size="icon"
            className={
              "rounded-[6px] border-[1px] text-innoveraZinc-900 w-[24px] h-[24px] group-hover:border-innoveraZinc-100 group-hover:bg-innoveraZinc-100 " +
              (isError
                ? "border-innoveraZinc-100 bg-innoveraZinc-100 group-hover:hover:bg-innoveraZinc-150"
                : "border-innoveraZinc-150 group-hover:hover:bg-innoveraZinc-150")
            }
            onClick={(e) => {
              e.stopPropagation();
              copyTaskIdToClipboard();
            }}
            title="Copy Task ID"
          >
            <Copy
              style={{
                width: "12px",
                height: "12px"
              }}
            />
          </AppButton>
        </TableCell>
        <TableCell className="font-medium max-w-[30px]">
          {costs && (
            <Tooltip>
              <TooltipTrigger>
                <AppButton
                  appearance="text"
                  size="icon"
                  className={
                    "rounded-[6px] border-[1px] text-innoveraZinc-900 w-[24px] h-[24px] group-hover:border-innoveraZinc-100 group-hover:bg-innoveraZinc-100 " +
                    (isError
                      ? "border-innoveraZinc-100 bg-innoveraZinc-100 group-hover:hover:bg-innoveraZinc-150"
                      : "border-innoveraZinc-150 group-hover:hover:bg-innoveraZinc-150")
                  }
                  title="Cost details"
                >
                  <Coins
                    style={{
                      width: "12px",
                      height: "12px"
                    }}
                  />
                </AppButton>
              </TooltipTrigger>
              <TooltipContent>
                <Costs
                  source={costs.source}
                  inputTokens={costs.inputTokens}
                  cachedInputTokens={costs.cachedInputTokens}
                  outputTokens={costs.outputTokens}
                  cost={costs.totalCost}
                  sourceCost={costs.costSource}
                  cacheHit={costs.cacheHit}
                />
              </TooltipContent>
            </Tooltip>
          )}
        </TableCell>
        <TableCell className="font-medium min-h-[56px] max-w-[334px] overflow-hidden">
          <FigmaTypography variant="label-2" className="text-zinc-900 truncate max-w-[334px] leading-normal" title={task}>
            {task}
          </FigmaTypography>
        </TableCell>
        <TableCell className="font-medium min-h-[56px]">
          <FigmaTypography
            variant="label-2"
            className="text-zinc-900 max-w-[176px] overflow-hidden text-ellipsis whitespace-nowrap"
            title={logGroup.rows[0]?.project?.name || logGroup.rows[0]?.chapter?.project.name}
          >
            {projectField}
          </FigmaTypography>
        </TableCell>
        <TableCell className="font-medium min-h-[56px]">
          <FigmaTypography variant="label-2" className="text-zinc-900" title={uniqueChapters.join(", ")}>
            {logGroup.rows[0]?.chapter
              ? logGroup.rows[0]?.chapter?.id + ": " + logGroup.rows[0]?.chapter?.projectTemplateChapterSlug
              : ""}
            {uniqueChapters.length > 1 && (
              <Badge
                variant="secondary"
                className={`ml-1 inline-flex items-center gap-1 rounded-[12px] bg-zinc-100 text-zinc-700`}
              >
                + {uniqueChapters.length - 1}
              </Badge>
            )}
          </FigmaTypography>
        </TableCell>
        <TableCell className="font-medium min-h-[56px]">
          <FigmaTypography variant="label-2" className="text-zinc-900">
            {logGroup.rows[0]?.user?.firstName && logGroup.rows[0]?.user?.lastName
              ? `${logGroup.rows[0]?.user?.firstName} ${logGroup.rows[0]?.user?.lastName}`
              : ""}
          </FigmaTypography>
        </TableCell>

        <TableCell className="font-medium min-h-[56px]">
          <Badge variant="secondary" className="mr-2 flex items-center gap-1 rounded-[4px] bg-zinc-100 px-[4px]">
            <CalendarBlank size={14} className="text-zinc-400" />
            {new Date(logGroup.rows[0].createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true
            })}
          </Badge>
        </TableCell>
        <TableCell className="font-medium flex items-center justify-between min-h-[56px]">
          <ExecutionTimeBadge executionTime={getRequestExecutionTime(logGroup)} />
          <span className="ml-2">
            {expanded ? (
              <ChevronUpIcon className="w-4 h-4 text-zinc-500 transition-transform duration-200" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-zinc-500 transition-transform duration-200" />
            )}
          </span>
        </TableCell>
      </TableRow>
      <AnimatePresence>
        {expanded && (
          <TableRow>
            <TableCell colSpan={9} className="bg-zinc-50 p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <div className="px-[16px] py-[8px]">
                  <AiLogGroupTable rows={logGroup.rows} onDetailClick={onDetailClick} />
                </div>
              </motion.div>
            </TableCell>
          </TableRow>
        )}
      </AnimatePresence>
    </>
  );
}
